/**
 * WHAT A RECORD REFERENCE IS ALLOWED TO SAY (#2325 §3, on top of #1705).
 *
 * A separate module from `queries.ts` for `recentsEmptyState.ts`'s reason: the
 * decision is four booleans and a noun, it decides what a person reads about a
 * record they may or may not be allowed to know exists, and it is worth being
 * able to state every case without mounting a query client.
 *
 * ── the four answers, and why there are four ───────────────────────────────
 * `core.titles_of` (#1472 / ADR F-33) is SECURITY INVOKER and returns NOTHING
 * for a record the caller cannot see, for an id that does not exist, and for an
 * entity with no title declaration. Those three are indistinguishable ON
 * PURPOSE — that is what makes non-disclosure indistinguishable from absence.
 *
 * But the THIRD is distinguishable from outside the door, and the metamodel
 * already says so: `core.entity_identity.title_declared` states whether the
 * entity has a title expression at all. So:
 *
 *   row with a title      → NAMED         the governed display name
 *   row with a NULL title → UNNAMED       authorized; the expression produced
 *                                         nothing (an organization saved with
 *                                         neither a legal nor a display name)
 *   no row, entity declares no title
 *                         → UNNAMED       authorized as far as anything here
 *                                         can know, and structurally unnameable
 *   no row, entity DOES declare one
 *                         → UNAVAILABLE   deleted, or the caller lost access
 *   no row, entity UNKNOWN to the registry
 *                         → UNAVAILABLE   a stale pin from a retired module;
 *                                         nothing governed calls this an entity
 *
 * The ruling asks for both halves and they pull opposite ways: §3 wants a
 * neutral `Unnamed {type}` for *"an authorized record with no resolvable
 * display label"*, and §2 wants a deleted or refused record *"removed/refused
 * without exposing its former label"*. Collapsing the two — which is what
 * `resolved` alone did — satisfies exactly one of them, and which one depends
 * on whether the surface filters or renders. Neither choice was made; it fell
 * out of a boolean.
 *
 * ── what is NEVER an answer ────────────────────────────────────────────────
 * The uuid. Not as a label, not as a fallback, not as a title attribute. That
 * is #1705's ruling and this module is where it is enforceable: there is no
 * branch here that can return an id, because an id is not one of its inputs.
 *
 * ── a failed READ is not a fact about the record ───────────────────────────
 * When the door did not answer at all, every ref is UNAVAILABLE — including the
 * structurally-unnameable ones. Saying `Unnamed quote` while the read is broken
 * would be asserting the record exists on the strength of a request that never
 * completed, and the surface above has an honest sentence for an outage
 * (`recentsEmptyState`).
 */

export type RecordNamingState = 'loading' | 'named' | 'unnamed' | 'unavailable'

export interface RecordNaming {
  state: RecordNamingState
  /** What a person reads. Never an id, in any state. */
  title: string
}

export interface RecordNamingInput {
  /** The row `core.titles_of` returned for this ref, or `undefined` for none. */
  row: { title: string | null } | undefined
  /**
   * The registry has an identity row for this entity key at all.
   *
   * A key nothing governed recognises — a stale pin from a retired module, a
   * hand-edited setting — cannot be described as "authorized and unnameable",
   * because nothing here knows it is an entity. It is UNAVAILABLE, and that is
   * a live shape: `accounts.spec.ts` pins one deliberately.
   */
  entityKnown: boolean
  /** `core.entity_identity.title_declared` — does the ENTITY have a title at all? */
  titleDeclared: boolean
  /** The entity's governed singular label, already localized by the registry. */
  typeLabel: string
  /** The batch read is still in flight. */
  pending: boolean
  /** The batch read failed. Distinct from "answered with nothing". */
  failed?: boolean
}

export function recordNaming(
  { row, entityKnown, titleDeclared, typeLabel, pending, failed = false }: RecordNamingInput,
): RecordNaming {
  if (row?.title) return { state: 'named', title: row.title }
  /* A row came back and its title expression yielded nothing. The caller may
     read this record — the door proved that by returning it — so this is a fact
     about the record, not a failure. */
  if (row) return { state: 'unnamed', title: unnamedTitle(typeLabel) }
  if (pending) return { state: 'loading', title: 'Loading record…' }
  if (failed) return { state: 'unavailable', title: 'Record unavailable' }
  /* Nothing governed recognises this entity key, so there is no "type" to be
     unnamed of — `typeLabel` here is the structural fallback, not a registry
     answer. Fail closed rather than inventing a noun (rule 6's shape, one tier
     out of SQL). */
  if (!entityKnown) return { state: 'unavailable', title: 'Record unavailable' }
  /* No row, and the entity declares no title for the door to resolve. Every
     record of this entity looks like this, so it is structural rather than a
     statement about whether THIS one exists. */
  if (!titleDeclared) return { state: 'unnamed', title: unnamedTitle(typeLabel) }
  return { state: 'unavailable', title: 'Record unavailable' }
}

/**
 * `Unnamed organization`, not `Unnamed Organization` and never `Organization
 * 3f2a…`. The noun is the registry's own localized singular, lower-cased by the
 * locale rather than by an ASCII assumption — Turkish `I` is the standing
 * counter-example, and the label is data a deployment can set.
 */
function unnamedTitle(typeLabel: string): string {
  return unnamedRecordTitle(typeLabel)
}

/**
 * The same sentence, for a caller that does not hold the registry's noun.
 *
 * A governed search door answers rows, not identities: it can return a row
 * whose text columns are all empty, and the caller has the row and the entity
 * but not always that entity's singular label. `Unnamed record` is the
 * truthful degradation of `Unnamed {type}` — it drops the noun rather than
 * inventing one, and it is still never the uuid, which is the half of #1705
 * that matters. ONE definition, because two copies of a ruling's wording is how
 * one of them stops following it (rule 42).
 */
export function unnamedRecordTitle(typeLabel?: string | null): string {
  const noun = typeLabel?.trim()
  return noun ? `Unnamed ${noun.toLocaleLowerCase()}` : 'Unnamed record'
}

/** A ref the surface may draw a row for. The inverse is a stale or refused item. */
export function isListable(state: RecordNamingState): boolean {
  return state !== 'unavailable'
}
